import messaging from '@react-native-firebase/messaging';
import { Image } from 'expo-image';
import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { Permission, PermissionsAndroid, Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetMeMutation, useNpoOnboarding } from '@/api/hooks/meHooks';
import { bell } from '@/assets/images';
import BrandedButton from '@/components/buttons/BrandedButton';
import Text from '@/components/Text';
import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { useAuth } from '@/contexts/authContext';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

const NotificationsScreen = () => {
  const { from, buyingShow } = useLocalSearchParams<{ from: string; buyingShow: string }>();

  const { state, dispatch, setPreferences, setChurchPreferences, setSecularPreferences } = useAuth();
  const { setCurrentUser } = useAuth();

  const { mutateAsync: onBoardingUpdate, isPending } = useNpoOnboarding(state.signUpData?.loginEmail?.type ?? 'CHURCH');
  const { mutateAsync: getMe } = useGetMeMutation();

  const colors = useColor();
  const [isLoading, setIsLoading] = useState(false);

  const titleText = i18n.t('sign_in.notification.title');
  const descriptionText = i18n.t('sign_in.notification.description');
  const buttonText = i18n.t('sign_in.notification.button');

  useEffect(() => {
    const type = state.signUpData?.loginEmail?.type;
    // Setting the ME user data for the first time because AuthStateChange triggers before we have data in our BE.
    // So we have to fill the user with the data of the onboarding compleated
    getMe(
      {
        firebaseId: state.userId ?? '',
        onBoardingType: type === 'CHARITIES' || type === 'CHURCH' ? 'npo' : 'individual'
      },
      {
        onSuccess: (data) => {
          setCurrentUser({
            ...data.user,
            location: data.onBoardingPreference?.location ?? '',
            searchRange: data.onBoardingPreference?.searchRange ?? 100,
            onBoardingId: data.onBoardingPreference?.id ?? '',
            address: data.onBoardingPreference?.address ?? '',
            subscribedToDonationChatRooms: data.onBoardingPreference?.subscribedToDonationChatRooms ?? ''
          });

          dispatch({
            type: 'SIGN_IN',
            userId: state.userId ?? data.user.firebaseId ?? '',
            providerId: state.providerId ?? '',
            phone: state.phone ?? data.user.phone ?? '',
            user: data
          });

          if (type === 'CHARITIES') {
            setSecularPreferences({
              programTypes: data.onBoardingPreferenceSecular?.at(0)?.programTypes ?? []
            });
          } else if (type === 'CHURCH') {
            setChurchPreferences({
              programTypes: data.onBoardingPreferenceChurch?.at(0)?.programTypes ?? [],
              denominationTypes: data.onBoardingPreferenceChurch?.at(0)?.denominationTypes ?? [],
              religionTypes: data.onBoardingPreferenceChurch?.at(0)?.religionTypes ?? []
            });
          } else {
            setPreferences({
              programTypes: data.onBoardingPreference?.programTypes ?? [],
              denominationTypes: data.onBoardingPreference?.denominationTypes ?? [],
              religionTypes: data.onBoardingPreference?.religionTypes ?? []
            });
          }
        },
        onError: (e) => {
          console.warn('ERROR', e);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPress = async () => {
    setIsLoading(true);
    try {
      if (Platform.OS === 'android' && Platform.Version >= 33)
        await PermissionsAndroid.request('android.permission.POST_NOTIFICATIONS' as Permission);
      else if (Platform.OS === 'web') Notification.requestPermission();
      else await messaging().requestPermission();

      if (from === 'nonProfit') {
        onBoardingUpdate(
          {
            preferenceId: state.preferenceId ?? '',
            isSetNotificationsScreen: true
          },
          {
            onSuccess: () => {
              router.replace({ pathname: '/login/location', params: { from: from } });
            }
          }
        );
      } else {
        router.replace({ pathname: '/login/location', params: { from: from, buyingShow } });
      }
    } catch (e) {
      console.error('Error requesting notification permission', e);
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.contentWrapper}>
        <Text style={[styles.titleText, { color: colors.titleText }]} fontWeight="700">
          {titleText}
        </Text>
        <Image source={bell} style={styles.image} />
        <Text style={styles.descriptionText}>{descriptionText}</Text>
      </View>
      <BrandedButton loading={isLoading || isPending} style={styles.btnStyle} onPress={onPress}>
        {buttonText}
      </BrandedButton>
    </SafeAreaView>
  );
};

export default NotificationsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginHorizontal: 16
  },
  contentWrapper: {
    flex: 1,
    alignItems: 'center'
  },
  titleText: {
    fontSize: 22,
    marginBottom: 26,
    textAlign: 'center',
    marginTop: 142
  },
  image: {
    height: 148,
    width: 148,
    marginBottom: 25
  },
  descriptionText: {
    textAlign: 'center'
  },
  btnStyle: {
    width: '100%',
    marginBottom: SIGN_IN_BUTTON_BOTTOM
  },
  btnLabel: {
    fontSize: 14
  }
});
